import { useCallback, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { useMediaQuery } from "../answer/hooks"
import type { MatterSessionType } from "../contract"
import { Button } from "../ds"
import { MatterPicker } from "./MatterPicker"
import "./WorkspaceCard.css"

export interface WorkspaceCardProps {
  type: MatterSessionType
  sessionId: string
  matter: { id: string; title: string } | null
  onChange: (m: { id: string; title: string } | null) => void
  /** "float": fixed in the bottom-right of the viewport. "rail": a block in the page's own right column. */
  placement?: "float" | "rail"
}

const WIDE = "(min-width: 1200px)"
// The floating card sits right of the centred 720px answer column (its edge is at 50vw + 360px), so it
// needs the viewport to clear that plus its own 300px and gutters; below this it is the pill.
const WIDE_FLOAT = "(min-width: 1400px)"
// The picker's outside mousedown closes it; the card's own buttons keep their click as the only toggle.
const keepToggle = (e: React.MouseEvent) => e.stopPropagation()

/**
 * Where this session sits in its workspace: a card in the empty right side of the page on wide
 * screens, a pill that opens the same card as a sheet on narrow ones. The picker (pick, create,
 * change, remove) is the one MatterPicker, so the assign logic and its analytics stay in one place.
 */
export function WorkspaceCard({ type, sessionId, matter, onChange, placement = "float" }: WorkspaceCardProps) {
  const wide = useMediaQuery(placement === "float" ? WIDE_FLOAT : WIDE)
  const [picking, setPicking] = useState(false)
  const [sheet, setSheet] = useState(false)
  const close = useCallback(() => setPicking(false), [])
  const here = useLocation()

  // In the rail the actions are small text links on one line, like the rows beside it.
  const inRail = placement === "rail"
  const body = matter ? (
    <>
      <p className="nr-wsc-over">Workspace</p>
      <p className="nr-wsc-name">{matter.title}</p>
      <div className="nr-wsc-acts">
        <Link className="nr nr-btn" data-variant={inRail ? "link" : "secondary"} data-size="sm" to={`/case/${encodeURIComponent(matter.id)}`}>Open workspace</Link>
        <Button variant={inRail ? "link" : "secondary"} size="sm" onMouseDown={keepToggle} onClick={() => setPicking(true)}>Change</Button>
      </div>
    </>
  ) : (
    <>
      <p className="nr-wsc-over">Workspace</p>
      <p className="nr-wsc-text">Keep this with the rest of the case. Files in a workspace are shared by every session in it, so you add them once.</p>
      <div className="nr-wsc-acts">
        <Button variant={inRail ? "link" : "primary"} size="sm" onMouseDown={keepToggle} onClick={() => setPicking(true)}>Add to workspace</Button>
      </div>
      <Link className="nr-wsc-about" to="/workspaces/about" state={{ from: here.pathname + here.search }}>What&rsquo;s a workspace?</Link>
    </>
  )

  const pop = picking ? (
    <div className="nr-wsc-pop">
      <MatterPicker
        type={type}
        sessionId={sessionId}
        currentCaseId={matter?.id ?? null}
        onDone={(m) => { onChange(m); setPicking(false) }}
        onClose={close}
      />
    </div>
  ) : null

  // The rail placement is a block in the page's own column at every width: a fixed pill would sit over its content.
  if (wide || placement === "rail") {
    return (
      <aside className={`nr nr-wsc nr-wsc-${placement}`} aria-label="Workspace">
        {body}
        {pop}
      </aside>
    )
  }

  return (
    <>
      <button
        type="button"
        className="nr nr-wsc-pill"
        data-attached={matter ? "" : undefined}
        aria-haspopup="dialog"
        aria-expanded={sheet}
        onMouseDown={keepToggle}
        onClick={() => setSheet((s) => !s)}
      >
        {matter ? matter.title : "Workspace"}
      </button>
      {sheet ? (
        <aside className="nr nr-wsc nr-wsc-sheet" aria-label="Workspace">
          {body}
          {pop}
          <div className="nr-wsc-close">
            <Button variant="quiet" size="sm" onClick={() => setSheet(false)}>Close</Button>
          </div>
        </aside>
      ) : null}
    </>
  )
}
